import { bookingFingerprint } from "../core/idempotency";
import type {
  AvailabilitySnapshot,
  BookingMutationInput,
  BookingMutationResult,
  BookingRecord,
  IdempotencyRecord,
} from "../core/types";
import type { CrmPort } from "./crm";
import {
  IdempotencyConflictError,
  SlotUnavailableError,
  StaleSnapshotError,
} from "./crm";

export class MockCrm implements CrmPort {
  private version = 1;
  private readonly slots = new Set<string>();
  private readonly records = new Map<string, IdempotencyRecord>();
  private mutationCount = 0;
  private bookingSequence = 0;

  constructor(initialSlots: readonly string[]) {
    for (const slot of initialSlots) this.slots.add(slot);
  }

  async getAvailability(): Promise<AvailabilitySnapshot> {
    return {
      version: this.version,
      slots: [...this.slots].sort(),
    };
  }

  async getIdempotencyRecord(key: string): Promise<IdempotencyRecord | null> {
    return this.records.get(key) ?? null;
  }

  async bookAppointment(
    input: BookingMutationInput,
  ): Promise<BookingMutationResult> {
    const fingerprint = bookingFingerprint(input);
    const existing = this.records.get(input.idempotencyKey);

    if (existing) {
      if (existing.fingerprint !== fingerprint) {
        throw new IdempotencyConflictError();
      }
      return {
        booking: existing.booking,
        replayed: true,
      };
    }

    if (input.expectedVersion !== this.version) {
      throw new StaleSnapshotError();
    }
    if (!this.slots.has(input.slot)) {
      throw new SlotUnavailableError();
    }

    this.bookingSequence += 1;
    const booking: BookingRecord = {
      bookingId: `synthetic-booking-${String(this.bookingSequence).padStart(3, "0")}`,
      sessionId: input.sessionId,
      slot: input.slot,
      idempotencyKey: input.idempotencyKey,
    };

    this.slots.delete(input.slot);
    this.version += 1;
    this.mutationCount += 1;
    this.records.set(input.idempotencyKey, {
      key: input.idempotencyKey,
      fingerprint,
      booking,
    });

    return {
      booking,
      replayed: false,
    };
  }

  advanceAvailabilityVersion(): void {
    this.version += 1;
  }

  getMutationCount(): number {
    return this.mutationCount;
  }

  getBookings(): readonly BookingRecord[] {
    return [...this.records.values()].map((record) => record.booking);
  }
}
